import { useState } from "react";
import { SlidersHorizontal, RotateCcw } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL = "all";
const SELECTS = ["category", "outcome", "source", "pos_match", "review_status", "photo_status"];

/** Month-to-date on the business day, everything else open. */
export function defaultFilters(scope, preset = {}) {
    const to = scope?.business_date || "";
    const base = { date_from: to ? `${to.slice(0, 8)}01` : "", date_to: to, branch_id: ALL, show_duplicates: false };
    SELECTS.forEach((k) => { base[k] = ALL; });
    return { ...base, ...preset };
}

/** Filters → the `GET /co/records` parameters (the totals strip and the export read the same). */
export function toParams(f, fallback) {
    const p = { date_from: f.date_from || fallback.date_from, date_to: f.date_to || fallback.date_to };
    ["branch_id", ...SELECTS].forEach((k) => { if (f[k] && f[k] !== ALL) p[k] = f[k]; });
    if (f.show_duplicates) p.show_duplicates = 1;
    return p;
}

function optionsFor(scope, t, lang) {
    const L = scope?.labels || {};
    const named = (rows) => (rows || []).map((r) => ({ value: r.key, label: lang === "ar" ? r.name_ar : r.name_en }));
    return {
        category: named(L.categories),
        outcome: named(L.outcomes),
        source: named(L.sources),
        pos_match: ["matched", "not_found", "ambiguous", "unavailable"].map((k) => ({ value: k, label: t(`co_rec_pos_${k}`) })),
        review_status: [{ value: "open", label: t("co_rec_review_open") }, ...named(L.review_statuses),
            { value: "none", label: t("co_rec_review_none") }],
        photo_status: [{ value: "stored", label: t("co_rec_photo_stored") }, { value: "none", label: t("co_rec_photo_none") }],
    };
}

function FilterSelect({ name, label, value, options, onChange, ids, allLabel }) {
    return (
        <div className="min-w-0 space-y-1">
            <span className="block text-[11px] font-bold text-[color:var(--ink-muted)]">{label}</span>
            <Select value={value || ALL} onValueChange={onChange}>
                <SelectTrigger data-testid={ids.filter(name)} className="h-10 w-full text-xs">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={ALL} data-testid={ids.option(name, ALL)}>{allLabel}</SelectItem>
                    {options.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={ids.option(name, o.value)}>{o.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

function DateField({ label, testId, value, onChange, min, max }) {
    return (
        <label className="min-w-0 space-y-1">
            <span className="block text-[11px] font-bold text-[color:var(--ink-muted)]">{label}</span>
            <Input type="date" dir="ltr" data-testid={testId} value={value} min={min || undefined} max={max || undefined}
                   onChange={(e) => onChange(e.target.value)} className="h-10 text-xs" />
        </label>
    );
}

/** Date range + branch always visible; the rest behind «الفلاتر (n)» so the list stays on the first screen at 390 px. */
export function RecordFilters({ scope, value, onChange, ids, lockReview, preset }) {
    const { t, tn, lang } = useI18n();
    const [open, setOpen] = useState(false);
    const opts = optionsFor(scope, t, lang);
    const keys = SELECTS.filter((k) => !(lockReview && k === "review_status"));
    const active = keys.filter((k) => value[k] && value[k] !== ALL).length + (value.show_duplicates ? 1 : 0);
    const set = (k) => (v) => onChange({ ...value, [k]: v });
    const branches = scope?.branches || [];
    const branchOpts = branches.map((b) => ({ value: b.id, label: tn(b) }));

    return (
        <div className="space-y-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <DateField label={t("co_rec_from")} testId={ids.dateFrom} value={value.date_from} max={value.date_to} onChange={set("date_from")} />
                <DateField label={t("co_rec_to")} testId={ids.dateTo} value={value.date_to} min={value.date_from}
                           max={scope?.business_date} onChange={set("date_to")} />
                {branches.length > 1 ? (
                    <FilterSelect name="branch" label={t("co_rec_field_branch")} value={value.branch_id} options={branchOpts}
                                  onChange={set("branch_id")} ids={{ ...ids, filter: () => ids.branch }} allLabel={t("co_rec_all_branches")} />
                ) : null}
                <div className="flex items-end">
                    <button type="button" data-testid={ids.filtersToggle} data-open={open ? "true" : "false"} onClick={() => setOpen((v) => !v)}
                            className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-full border border-[color:var(--border-soft)] px-3 text-xs font-bold text-[color:var(--brand-brown)] transition-[background-color] duration-150 hover:bg-[color:var(--brand-orange-soft)]">
                        <SlidersHorizontal className="h-4 w-4" />
                        {t("co_rec_filters")}{active ? ` (${active})` : ""}
                    </button>
                </div>
            </div>
            {open ? (
                <>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                        {keys.map((k) => (
                            <FilterSelect key={k} name={k} label={t(`co_rec_filter_${k}`)} value={value[k]} options={opts[k]}
                                          onChange={set(k)} ids={ids} allLabel={t("co_rec_all")} />
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <label className="inline-flex items-center gap-2 text-xs font-semibold text-[color:var(--ink)]">
                            <Switch data-testid={ids.duplicates} checked={!!value.show_duplicates} onCheckedChange={set("show_duplicates")} />
                            {t("co_rec_show_duplicates")}
                        </label>
                        <button type="button" data-testid={ids.reset} onClick={() => onChange(defaultFilters(scope, preset))}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]">
                            <RotateCcw className="h-3.5 w-3.5" />{t("co_rec_reset")}
                        </button>
                    </div>
                </>
            ) : null}
        </div>
    );
}
